import { CssBaseline, CloudinaryProvider, GoogleMapsProvider, SnackbarProvider, ThemeProvider } from '@paintscout/ui'
import { UsersProvider, useUser, useClientOptions, useUserPermissions } from '@paintscout/ui-business'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { track } from 'shared/util/analytics'
import { LocationTrackerProvider as LocationTracker } from '@paintscout/react-quote'
import { motion, useAnimation } from 'framer-motion'
import React, { Suspense, useEffect } from 'react'
import { AuthProvider, markSkipAuthCache } from 'react-auth0'
import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'
import { ConnectionProvider } from 'react-detect-offline'
import type { RouteComponentProps } from 'react-router-dom'
import { Redirect, Router, Route as SimpleRoute, Switch, useLocation } from 'react-router-dom'
import { audience, auth, clearUserData, scope } from 'shared/util/auth'
import history from 'shared/util/routing/history'
import ErrorBoundary from 'src/components/common/ErrorBoundary'
import Header, { SideNav } from 'src/components/common/Header'
import 'src/util/appSentry'
import { getTheme } from 'src/util/theme'
import migrateOldKeys from '../../util/localstorage'
import RedirectHome from '../common/RedirectHome'
import Route from '../common/Route'
import TrialExpiredGuard from '../common/TrialExpiredGuard'
import SubscriptionGuard from '../common/SubscriptionGuard'
import Login from '../pages/Login'
import LoginCallback from '../pages/LoginCallback'
import Logout from '../pages/Logout'
import DashboardComponent from './DashboardComponent'
// For some reason, this was causing an error... I don't know why it would be different than the other lazy loaded components, though. Maybe because it gets hit before  the app has loaded?
// const Signup = React.lazy(() => import('../pages/Signup'))
import Signup from '../pages/Signup'

// Quote pages (highest impact ~1.5MB)
const EditQuote = React.lazy(() => import('../pages/quotes/EditQuote'))
const NewQuote = React.lazy(() => import('../pages/quotes/NewQuote/NewQuote'))
const NewQuoteTemplate = React.lazy(() => import('../pages/quotes/NewQuoteTemplate'))
const QuoteList = React.lazy(() => import('../pages/quotes/QuoteList'))

// Scheduling/events pages
const Events = React.lazy(() => import('../pages/events'))
const Scheduling = React.lazy(() => import('../pages/scheduling'))
const PainterSchedulePage = React.lazy(() => import('painter-view/components/PainterSchedulePage/PainterSchedulePage'))

// People/deals pages
const EditDeal = React.lazy(() => import('../pages/deals/EditDeal'))
const DealView = React.lazy(() => import('../pages/deals/DealView'))
const EditContact = React.lazy(() => import('../pages/people/EditContact'))
const EditCompany = React.lazy(() => import('../pages/people/EditCompany'))
const PeopleList = React.lazy(() => import('../pages/people/PeopleList'))
const CompanyList = React.lazy(() => import('../pages/people/CompanyList'))

// Remaining pages
const Locations = React.lazy(() => import('../pages/locations/Locations'))
const ReportRemoteView = React.lazy(() => import('../pages/view/ReportRemoteView'))
const Tasks = React.lazy(() => import('../pages/tasks'))
import ApiClientProvider from './ApiClientProvider'
import AuthorizeUser from './AuthorizeUser'
import CheckQuoteRecovery from './CheckQuoteRecovery'
import CRMTrialSync from './CRMTrialSync'
import DialogStackProviderWithRouter from './DialogStackProviderWithRouter'
import { PaymentFailureProvider } from 'src/components/dialogs/PaymentFailureDialog'
import HubspotChat from './HubspotChat'
import LogRocket from './LogRocket'
import PostHogIdentify from './PostHogIdentify'
import OnboardUser from './OnboardUser'
import ScrollToTop from './ScrollToTop'
import UpdateChecker from './UpdateChecker'
import UserFlow from 'shared/components/common/UserFlow'
import GitBookDocs from 'shared/components/common/GitBookDocs'
import UserflowCloseButton from 'shared/components/common/UserflowCloseButton'
import HubSpotFormProxy from '../pages/HubSpotFormProxy'
const IncomingIntegrationLink = React.lazy(() => import('../pages/integrate/IncomingIntegrationLink'))
const Profile = React.lazy(() => import('../pages/profile/Profile/Profile'))
const Settings = React.lazy(() => import('../pages/settings/Settings/Settings'))
const Reports = React.lazy(() => import('../pages/reports/Reports/Reports'))
const Update = React.lazy(() => import('../pages/Update'))
const PresentationEdit = React.lazy(() => import('../pages/presentations/PresentationEdit'))

const GenericReport = React.lazy(() => import('../pages/reports/Reports/GenericReport'))

// migrate old ls keys
migrateOldKeys()

const defaultTheme = getTheme()

function getPageName(path: string): string {
  const clean = path.replace(/^\/+|\/+$/g, '')
  if (!clean) return 'home'
  return clean
    .replace(/\/[a-f0-9]{32}/g, '') // remove CouchDB-style IDs
    .replace(/\/[a-f0-9-]{36}/g, '') // remove UUIDs
    .replace(/\/+$/, '')
    .replace(/\//g, '_')
}

// Toggle for the manual `page_viewed` event. Disabled by default to control
// PostHog event volume/cost. Set POSTHOG_PAGE_VIEW_TRACKING=true (e.g. via the
// AWS Parameter Store env var) to re-enable without removing the code below.
const PAGE_VIEW_TRACKING_ENABLED = process.env.POSTHOG_PAGE_VIEW_TRACKING === 'true'

function PageViewTracker() {
  const location = useLocation()

  useEffect(() => {
    if (!PAGE_VIEW_TRACKING_ENABLED) return
    track('page_viewed', {
      path: location.pathname,
      pageName: getPageName(location.pathname)
    })
  }, [location.pathname])

  return null
}

function App() {
  const detectOfflineConfig = {
    url: `${process.env.PS_API_URL}/ps-ping`
  }

  return (
    <>
      {/* keep this as high up the tree as possible, lessens possibility of an error causing this not to mount */}
      <UpdateChecker />

      <Router history={history}>
        {/* applyUpdatesOverNavigation flag retired (issue #13840) — was off for everyone; PendingUpdateHandler removed. */}
        <Switch>
          {/* 
          We put this route high up the tree so that there's little chance for an error from a parent component to break it.
          <SimpleRoute /> is just a regular react-router Route (our custom Route component will not work here)
        */}
          <SimpleRoute path="/update" exact component={Update} />
        </Switch>
        <ConnectionProvider config={detectOfflineConfig}>
          <CloudinaryProvider
            cloudName={process.env.CLOUDINARY_CLOUD_NAME}
            uploadPreset={process.env.CLOUDINARY_UPLOAD_PRESET}
            secure={true}
          >
            <ThemeProvider theme={defaultTheme}>
              <SnackbarProvider>
                <CssBaseline />
                <ScrollToTop />
                <PageViewTracker />
                <ErrorBoundary isRootBoundary>
                  <LocationTracker>
                    <AuthProvider
                      auth0Client={auth}
                      scope={scope}
                      audience={audience}
                      onLogout={(companyId) => clearUserData(companyId)}
                      appUrl={process.env.PS_APP_URL}
                    >
                      <ApiClientProvider>
                        <DialogStackProviderWithRouter>
                          <Switch>
                            {/* Unauthenticated Routes */}
                            <Route path="/signup" exact component={Signup} />
                            <Route path="/login/:slug?" component={Login} />
                            <Route path="/logout" exact component={Logout} />
                            <Route path="/login-callback" exact component={LoginCallback} />

                            {/* Authenticated Routes */}
                            <Route
                              path="/"
                              render={() => (
                                <AuthorizeUser>
                                  <AuthenticatedAppShell />
                                </AuthorizeUser>
                              )}
                            />
                          </Switch>
                        </DialogStackProviderWithRouter>
                      </ApiClientProvider>
                    </AuthProvider>
                  </LocationTracker>
                </ErrorBoundary>
              </SnackbarProvider>
            </ThemeProvider>
          </CloudinaryProvider>
        </ConnectionProvider>
      </Router>
    </>
  )
}

///////////////////////////
//        Routes        //
/////////////////////////

function QuoteRoutes({ match }: RouteComponentProps) {
  return (
    <Switch>
      <Route path={`${match.path}/new`} exact component={NewQuote} />
      <Route path={`${match.path}/edit/:id`} exact component={EditQuote} />
      <Route path={`${match.path}/new/template/:quoteType`} exact component={NewQuoteTemplate} />
      <Route path={`${match.path}/`} exact component={QuoteList} />
    </Switch>
  )
}

function DealRoutes({ match }: RouteComponentProps) {
  const { crmVisible } = useFlags()
  if (!crmVisible) {
    return <RedirectHome />
  }

  return (
    <Switch>
      <Route path={`${match.path}`} exact render={() => <Redirect to={`${match.path}/board`} />} />
      <Route path={`${match.path}/edit/:id`} exact component={EditDeal} />
      <Route path={`${match.path}/:view`} exact component={DealView} />
    </Switch>
  )
}

function EventRoutes({ match }: RouteComponentProps) {
  const { crmVisible } = useFlags()
  if (!crmVisible) {
    return <RedirectHome />
  }
  return (
    <Switch>
      <Route path={`${match.path}`} component={Events} />
    </Switch>
  )
}

function SchedulingRoutes({ match }: RouteComponentProps) {
  const { crm } = useFlags()

  return (
    <Switch>
      {!crm && <Route path={`${match.path}`} component={RedirectHome} />}
      <Route path={`${match.path}`} component={Scheduling} />
    </Switch>
  )
}

function AuthenticatedAppShell() {
  const { isPainter, isSuperadmin, isMasquerading } = useUser()

  // Only redirect genuine painter sessions to the standalone painter-view app. Don't redirect a
  // superadmin masquerading into / previewing a painter — masqueradeCompanyId is per-origin, so :3002
  // (which has no masquerade context) would resolve them as a non-painter and bounce them right back
  // here, causing an infinite ping-pong. Superadmins preview the painter schedule via the SideNav
  // "Painter View" link instead (rendered in-app by PainterAccountWrapper).
  const isRealPainter = isPainter && !isMasquerading && !isSuperadmin

  useEffect(() => {
    if (isRealPainter) {
      const painterViewUrl = process.env.PS_PAINTER_VIEW_URL
      if (!painterViewUrl) {
        console.error('[App] PS_PAINTER_VIEW_URL is not set — falling back to http://localhost:3002')
      }
      markSkipAuthCache()
      window.location.replace(painterViewUrl ?? 'http://localhost:3002')
    }
  }, [isRealPainter])

  // Render nothing while isPainter is loading (undefined) or while the redirect is pending — prevents
  // flashing the non-painter shell in the one-render gap before isPainter resolves to true.
  if (isPainter === undefined || isRealPainter) return null

  return (
    <GoogleMapsProvider>
      <UsersProvider>
        <DialogStackProviderWithRouter>
          {/* App-wide, not dashboard-only: a user who lands on /quotes and never opens the
              dashboard still has to be told their payment failed (ENG-1532). It also owns the
              dialog's open state, so the header banner's Pay Now can drive the same instance. */}
          <PaymentFailureProvider>
            <OnboardUser />
            <UserFlow />
            <UserflowCloseButton />
            <CheckQuoteRecovery />
            <LogRocket />
            <PostHogIdentify />
            <CRMTrialSync />
            <HubspotChat />
            <GitBookDocs />
            <PainterAccountWrapper>
              <SideNav>
                <>
                  <Header />
                  <TrialExpiredGuard>
                    <SubscriptionGuard>
                      <AnimatedSwitch>
                        <Route path="/dashboard" exact component={DashboardRoutes} auth />
                        <Route
                          path="/locations"
                          exact
                          component={Locations}
                          auth
                          roles={['corporate']}
                          blockMasquerade
                        />

                        <Route path="/deals" component={DealRoutes} auth />

                        <Route path="/quotes" component={QuoteRoutes} auth />
                        <Route
                          path="/invoices"
                          component={InvoiceRoutes}
                          auth
                          features={{ 'invoices.enabled': true }}
                        />
                        <Route path="/people" component={PeopleRoutes} auth />
                        <Route path="/profile/:category?" component={Profile} auth />
                        <Route
                          path="/settings/:section?"
                          component={Settings}
                          auth
                          permission={'write:account-settings'}
                          notAccessible
                        />

                        <Route
                          path="/integrate"
                          component={IntegrateRoutes}
                          auth
                          features={{ 'integrations.enabled': true }}
                        />

                        <Route path="/reports" component={ReportRoutes} auth />

                        <Route path="/view/:sendUrl/:silent?" component={ReportRemoteView} />

                        <Route path="/events" component={EventRoutes} auth />
                        <Route path="/tasks" component={TaskRoutes} auth />

                        <Route path="/new-presentation" component={PresentationRoutes} auth />
                        <Route path="/scheduling" component={SchedulingRoutes} auth />
                        <Route path="/hs-forms/:formId" exact component={HubSpotFormProxy} auth />
                        <Route path="/logout" exact component={Logout} auth />

                        <RedirectHome />
                      </AnimatedSwitch>
                    </SubscriptionGuard>
                  </TrialExpiredGuard>
                </>
              </SideNav>
            </PainterAccountWrapper>
          </PaymentFailureProvider>
        </DialogStackProviderWithRouter>
      </UsersProvider>
    </GoogleMapsProvider>
  )
}

function PainterAccountWrapper({ children }: { children: React.ReactNode }) {
  const { painterAccount } = useFlags()
  const { isSuperadmin, isMasquerading } = useUser()
  const location = useLocation()

  // Superadmin/masquerade preview via the SideNav link: render full-screen on the explicit painter route,
  // with an exit affordance back to the normal app. (PainterSchedulePage's forcePainterPreview kicks in
  // here since isPainter is false.)
  if (painterAccount && (isSuperadmin || isMasquerading) && location.pathname === '/scheduling/painter') {
    return (
      <>
        <Suspense fallback={null}>
          <PainterSchedulePage />
        </Suspense>
        <div
          style={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 9999,
            background: 'rgba(0,0,0,0.75)',
            color: '#fff',
            padding: '6px 14px',
            borderRadius: 4,
            fontSize: 13,
            cursor: 'pointer',
            userSelect: 'none'
          }}
          onClick={() => history.push('/scheduling')}
        >
          ← Exit Preview
        </div>
      </>
    )
  }

  return <>{children}</>
}

function TaskRoutes({ match }: RouteComponentProps) {
  const { crmVisible } = useFlags()
  if (!crmVisible) {
    return <RedirectHome />
  }
  return (
    <Switch>
      <Route path={`${match.path}`} exact component={Tasks} />
    </Switch>
  )
}

// invoices re-use the quote components, only difference is 'invoices' in URL
function InvoiceRoutes(props: RouteComponentProps) {
  // make sure we're mounting a different QuoteRoutes if we're switching between /quotes and /invoices
  return <QuoteRoutes {...props} />
}

function PresentationRoutes({ match }: RouteComponentProps) {
  const { presentationNext } = useFlags()
  if (!presentationNext) {
    return <RedirectHome />
  }

  return (
    <>
      <Route path={`${match.path}/:id`} exact component={PresentationEdit} permission={'write:account-settings'} />
    </>
  )
}

function PeopleRoutes({ match }: RouteComponentProps) {
  const READ_ALL_CONTACTS = 'read:all-contacts'
  return (
    <>
      <Route path={`${match.path}`} exact render={() => <Redirect to={`${match.path}/contacts`} />} />
      <Route path={`${match.path}/companies`} exact component={CompanyList} permission={READ_ALL_CONTACTS} />
      <Route path={`${match.path}/companies/:id`} exact component={EditCompany} permission={READ_ALL_CONTACTS} />
      <Route path={`${match.path}/contacts`} exact component={PeopleList} permission={READ_ALL_CONTACTS} />
      <Route path={`${match.path}/contact/:id`} exact component={EditContact} permission={READ_ALL_CONTACTS} />
    </>
  )
}

function DashboardRoutes(_: RouteComponentProps) {
  return <Route path="/dashboard" exact component={DashboardComponent} auth />
}

function IntegrateRoutes({ match }: RouteComponentProps) {
  return (
    <>
      <Route path={`${match.path}/:provider/:entity/`} exact component={IncomingIntegrationLink} />
    </>
  )
}

function ReportRoutes({ match }: RouteComponentProps) {
  const { crm: crmFlag } = useFlags()
  const { options } = useClientOptions()
  const reportsFeatureEnabled = options.options.features?.reports?.enabled
  const { permissionToViewReports } = useUserPermissions()

  if ((!crmFlag && !reportsFeatureEnabled) || !permissionToViewReports) {
    return <RedirectHome />
  }
  return (
    <>
      <Route path={`${match.path}`} exact component={Reports} />
      {/* 
        "Special" (ie: non-generic) reports can be put here.
        So far, we haven't needed that though.
        <Route path={`${match.path}/payments-collected`} component={PaymentsCollected} /> 
      */}
      <Route path={`${match.path}/:report`} component={GenericReport} />
    </>
  )
}

function AnimatedSwitch({ children }) {
  const location = useLocation()

  const controls = useAnimation()

  return (
    <Suspense
      fallback={
        // if the fallback is rendered, we know the page is being lazy loaded, so we know to trigger the fade-in
        <Mounter
          onMount={() => {
            // mount() is undocumented, not sure what it does other than it prevents an error about calling .set too soon
            controls.mount()

            controls.set({ opacity: 0 })
          }}
          onUnmount={() => {
            controls.start({ opacity: 1, transition: { duration: 0.3 } })
          }}
        />
      }
    >
      <motion.div animate={controls}>
        <Switch location={location}>{children}</Switch>
      </motion.div>
    </Suspense>
  )
}

function Mounter({ onMount, onUnmount }) {
  useEffect(() => {
    onMount()
    return () => onUnmount()
  }, [])
  return null
}

export default App
